import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useCurrency } from '../context/CurrencyContext';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Droplets, 
  TrendingUp, 
  TrendingDown,
  Wallet,
  AlertCircle,
  Loader2,
  ChevronLeft,
  Plus,
  Minus,
  PieChart,
  Clock,
  DollarSign,
  Shield,
  Zap,
  Check,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Slot Info Card
function SlotCard({ pool, isSelected, onSelect, disabled, topLPs }) {
  const { showUsd, stxoshiUsd } = useCurrency();
  const _U = (w) => showUsd ? `$${((w || 0) * (stxoshiUsd || 0.0000037)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (w || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const _unit = showUsd ? "USD" : "STXOSHI";
  const riskLevel = pool.slot <= 3 ? 'Low' : pool.slot <= 6 ? 'Medium' : 'High';
  const riskColor = pool.slot <= 3 ? 'text-green-400' : pool.slot <= 6 ? 'text-yellow-400' : 'text-red-400';
  
  return (
    <button
      onClick={() => !disabled && onSelect(pool.slot)}
      disabled={disabled}
      className={`
        w-full p-4 rounded-xl border-2 text-left transition-all
        ${isSelected 
          ? 'border-secondary bg-secondary/10 shadow-lg' 
          : 'border-border hover:border-secondary/50 bg-muted/30'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-heading text-2xl font-bold">{pool.slot}x</span>
          <Badge variant="outline" className={`${riskColor} border-current`}>
            {riskLevel} Risk
          </Badge>
        </div>
        <span className="font-mono text-lg">{pool.payout_multiplier}x</span>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Pool Liquidity</span>
          <span className="font-mono">
            {_U((pool.total_liquidity || 0) / 1_000_000)} {_unit}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Win Probability</span>
          <span className="font-mono">{pool.win_probability?.toFixed(2)}%</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Total Flips</span>
          <span className="font-mono">{pool.total_bets || 0}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">W/L Ratio</span>
          <span className="font-mono">
            <span className="text-green-400">{pool.win_count || 0}</span>
            /
            <span className="text-red-400">{pool.loss_count || 0}</span>
          </span>
        </div>
        
        {/* Top 3 LPs */}
        <div className="pt-2 mt-2 border-t border-border">
          <span className="text-muted-foreground text-xs">Top 3 LPs</span>
          {topLPs && topLPs.length > 0 ? (
            <div className="mt-1 space-y-1">
              {topLPs.map((lp, idx) => (
                <div key={idx} className="flex justify-between text-xs">
                  <span className="text-cyan-400 font-mono truncate max-w-[120px]" title={lp.user_address}>
                    {lp.wallet_masked || (lp.user_address ? `${lp.user_address.slice(0,5)}...${lp.user_address.slice(-4)}` : 'Unknown')}
                  </span>
                  <span className="text-secondary font-semibold">{lp.share_percentage?.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground/50 mt-1">No LPs yet</p>
          )}
        </div>
      </div>
    </button>
  );
}

// LP Position Card
function LPPositionCard({ position, onWithdraw, loading }) {
  const { showUsd, stxoshiUsd } = useCurrency();
  const _R = stxoshiUsd || 0.0000037;
  const _U = (w) => showUsd ? `$${((w || 0) * _R).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (w || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
  const _unit = showUsd ? "USD" : "STXOSHI";
  const profitLoss = position.net_profit / 1_000_000;
  const isProfit = profitLoss >= 0;
  
  return (
    <Card className="glass-card border-secondary/30">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
              <Droplets className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <p className="font-semibold">
                {position.slot === 0 ? 'All Slots' : `Slot ${position.slot}x`}
              </p>
              <p className="text-xs text-muted-foreground">
                {position.share_percentage?.toFixed(2)}% of pool
              </p>
            </div>
          </div>
          <Badge variant={isProfit ? 'default' : 'destructive'} className={isProfit ? 'bg-green-500/20 text-green-400' : ''}>
            {isProfit ? '+' : ''}{_U(profitLoss)} {_unit}
          </Badge>
        </div>
        
        <div className="grid grid-cols-3 gap-4 text-center mb-4">
          <div>
            <p className="text-xs text-muted-foreground">Deposited</p>
            <p className="font-mono text-sm">{_U(position.amount_stxoshi / 1_000_000)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Earned</p>
            <p className="font-mono text-sm text-green-400">+{_U(position.total_earned / 1_000_000)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Paid Out</p>
            <p className="font-mono text-sm text-red-400">-{_U(position.total_paid / 1_000_000)}</p>
          </div>
        </div>
        
        <Button
          variant="outline"
          onClick={() => onWithdraw(position.id)}
          disabled={loading || position.status === 'withdrawn'}
          className={`w-full ${position.status === 'withdrawn' ? 'border-green-500 text-green-400' : 'border-secondary text-secondary hover:bg-secondary/10'}`}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Withdrawing...
            </>
          ) : position.status === 'withdrawn' ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Withdrawn
            </>
          ) : (
            <>
              <Minus className="w-4 h-4 mr-2" />
              Withdraw Position
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function LPFlipsMarket() {
  const navigate = useNavigate();
  const { isConnected, user, connectWallet, getAuthHeaders, addresses } = useWallet();
  const { showUsd: lpShowUsd, stxoshiUsd: lpRate } = useCurrency();
  const lpU = (w) => lpShowUsd ? `$${((w || 0) * (lpRate || 0.0000037)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (w || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  
  // State
  const [pools, setPools] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(0); // 0 = all slots
  const [depositAmount, setDepositAmount] = useState('');
  const [myPositions, setMyPositions] = useState([]);
  const [marketStats, setMarketStats] = useState(null);
  const [lpSlotStats, setLpSlotStats] = useState(null); // For top 3 LPs per slot
  const [loading, setLoading] = useState(false);
  const [depositStatus, setDepositStatus] = useState('idle'); // idle, signing, confirming, complete
  const [withdrawingId, setWithdrawingId] = useState(null);
  
  // Fetch data on mount
  useEffect(() => {
    fetchPools();
    fetchStats();
    fetchLPSlotStats();
    if (isConnected) {
      fetchMyPositions();
    }
  }, [isConnected]);
  
  const fetchPools = async () => {
    try {
      const response = await fetch(`${API}/api/flip-market/pools`);
      const data = await response.json();
      if (data.success) {
        setPools(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch pools:', error);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/api/flip-market/stats`);
      const data = await response.json();
      if (data.success) {
        setMarketStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  const fetchLPSlotStats = async () => {
    try {
      const response = await fetch(`${API}/api/flip-market/admin-stats`);
      const data = await response.json();
      if (data.success) {
        setLpSlotStats(data.data.lp_slots);
      }
    } catch (error) {
      console.error('Failed to fetch LP slot stats:', error);
    }
  };
  
  const fetchMyPositions = async () => {
    try {
      const response = await fetch(`${API}/api/flip-market/lp/my-positions`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setMyPositions(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    }
  };
  
  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount < 1) {
      toast.error('Minimum deposit is 1 STXOSHI');
      return;
    }
    
    if (!addresses?.stx) {
      toast.error('Please connect your wallet first');
      return;
    }
    
    setLoading(true);
    setDepositStatus('signing');
    
    try {
      const amountMicro = Math.floor(amount * 1_000_000);
      
      // Step 1: Initiate deposit on backend to get treasury wallet
      const initResponse = await fetch(`${API}/api/flip-market/lp/initiate-deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          amount_stxoshi: amountMicro,
          slot: selectedSlot
        })
      });
      
      const initData = await initResponse.json();
      if (!initData.success) {
        throw new Error(initData.detail || 'Failed to initiate deposit');
      }
      
      const treasuryWallet = initData.treasury_wallet;
      const depositId = initData.deposit_id;
      
      // Step 2: Sign STXOSHI token transfer using sats-connect (same as swap sell)
      // Dynamic imports for wallet interaction
      const satsConnect = await import('sats-connect');
      const stacksTx = await import('@stacks/transactions');
      
      // STXOSHI contract
      const STXOSHI_CONTRACT = "SP1Z92MPDQEWZXW36VX71Q25HKF5K2EPCJ304F275.tokensoft-token-v4karmaedcr";
      
      // Build post-condition
      const postCondition = stacksTx.Pc.principal(addresses.stx)
        .willSendEq(BigInt(amountMicro))
        .ft(STXOSHI_CONTRACT, 'tokensoft-token');
      
      const payload = {
        contract: STXOSHI_CONTRACT,
        functionName: 'transfer',
        arguments: [
          stacksTx.cvToHex(stacksTx.uintCV(amountMicro)),
          stacksTx.cvToHex(stacksTx.principalCV(addresses.stx)),
          stacksTx.cvToHex(stacksTx.principalCV(treasuryWallet)),
          stacksTx.cvToHex(stacksTx.noneCV())
        ],
        postConditions: [stacksTx.postConditionToHex(postCondition)],
        postConditionMode: 'deny'
      };
      
      const response = await satsConnect.request('stx_callContract', payload);
      
      if (!response?.result?.txid) {
        throw new Error('Transaction cancelled or failed');
      }
      
      const txId = response.result.txid;
      
      // Step 3: Confirm deposit with txid
      setDepositStatus('confirming');
      const confirmResponse = await fetch(`${API}/api/flip-market/lp/confirm-deposit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          deposit_id: depositId,
          tx_id: txId
        })
      });
      
      const confirmData = await confirmResponse.json();
      if (confirmData.success) {
        setDepositStatus('complete');
        toast.success('Liquidity added successfully!', {
          description: `${amount.toLocaleString()} STXOSHI deposited to ${selectedSlot === 0 ? 'All Slots' : `Slot ${selectedSlot}`}`,
        });
        setDepositAmount('');
        fetchMyPositions();
        fetchPools();
        fetchStats();
        fetchLPSlotStats();
      } else {
        throw new Error(confirmData.detail || 'Failed to confirm deposit');
      }
    } catch (error) {
      console.error('Deposit error:', error);
      toast.error('Deposit failed', { description: error.message || 'Please try again' });
      setDepositStatus('idle');
    } finally {
      setLoading(false);
      setTimeout(() => setDepositStatus('idle'), 3000);
    }
  };
  
  // Manual confirm for stuck deposits - checks pending deposits and confirms them
  const handleManualConfirm = async () => {
    setLoading(true);
    try {
      // Check for pending deposits for this user
      const response = await fetch(`${API}/api/flip-market/lp/check-pending`, {
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.success && data.data?.pending_count > 0) {
        toast.success('Deposit Confirmed!', {
          description: `Found ${data.data.pending_count} pending deposit(s). They have been confirmed on-chain.`,
          duration: 5000
        });
        fetchMyPositions();
        fetchPools();
        fetchStats();
        fetchLPSlotStats();
      } else if (data.success && data.data?.confirmed_count > 0) {
        toast.success('Deposit Already Confirmed!', {
          description: `${data.data.confirmed_count} deposit(s) already confirmed on-chain.`,
          duration: 5000
        });
        fetchMyPositions();
        fetchPools();
        fetchStats();
        fetchLPSlotStats();
      } else {
        toast.info('No Pending Deposits', {
          description: 'No pending deposits found. Enter an amount and click "Deposit & Confirm On-Chain" first.',
          duration: 5000
        });
      }
    } catch (error) {
      toast.error('Check failed', { description: error.message });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleWithdraw = async (positionId) => {
    setWithdrawingId(positionId);
    try {
      const response = await fetch(`${API}/api/flip-market/lp/withdraw/${positionId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Withdrawal Complete!', {
          description: `${(data.data.withdrawal_amount / 1_000_000).toFixed(2)} STXOSHI sent to your wallet. TX: ${data.data.txid?.slice(0, 10)}...`,
          duration: 8000
        });
        fetchMyPositions();
        fetchPools();
        fetchStats();
        fetchLPSlotStats();
      } else {
        toast.error(data.detail || 'Withdrawal failed');
      }
    } catch (error) {
      toast.error('Withdrawal failed', { description: error.message });
      console.error(error);
    } finally {
      setWithdrawingId(null);
    }
  };
  
  // Calculate estimated APY based on market activity
  const calculateEstimatedAPY = (slot) => {
    // Higher slots have higher risk but potentially higher rewards
    // Base APY from 3% vig * volume turnover
    const baseAPY = 15; // Base 15% from vig
    const riskMultiplier = 1 + (slot * 0.1); // Higher slots = higher potential
    return (baseAPY * riskMultiplier).toFixed(1);
  };
  
  // Refresh all data
  const [refreshing, setRefreshing] = useState(false);
  const handleRefreshAll = async () => {
    setRefreshing(true);
    toast.info('Refreshing all LP data...');
    try {
      await Promise.all([
        fetchPools(),
        fetchStats(),
        fetchLPSlotStats(),
        isConnected ? fetchMyPositions() : Promise.resolve()
      ]);
      toast.success('All LP data refreshed!');
    } catch (error) {
      toast.error('Refresh failed', { description: error.message });
    } finally {
      setRefreshing(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/create')}
            className="mb-6"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Launch
          </Button>
          
          {/* Header with Refresh Button */}
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Droplets className="w-6 h-6 text-secondary" />
              <h1 className="font-heading text-2xl font-bold">LP Flips Market</h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefreshAll}
                disabled={refreshing}
                className="ml-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Provide liquidity • Earn from the 3% vig • Share in flipper losses
            </p>
          </div>
          
          {/* Market Stats */}
          {marketStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="glass-card">
                <CardContent className="p-4 text-center">
                  <PieChart className="w-6 h-6 text-primary mx-auto mb-2" />
                  <p className="text-2xl font-mono font-bold">
                    {lpU(marketStats.total_liquidity_stxoshi / 1_000_000)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total TVL ({lpShowUsd ? "USD" : "STXOSHI"})</p>
                </CardContent>
              </Card>
              
              <Card className="glass-card">
                <CardContent className="p-4 text-center">
                  <DollarSign className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-mono font-bold">
                    {lpU(marketStats.total_volume_stxoshi / 1_000_000)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Volume{lpShowUsd ? " (USD)" : ""}</p>
                </CardContent>
              </Card>
              
              <Card className="glass-card">
                <CardContent className="p-4 text-center">
                  <Zap className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <p className="text-2xl font-mono font-bold">{marketStats.total_bets}</p>
                  <p className="text-xs text-muted-foreground">Total Flips</p>
                </CardContent>
              </Card>
              
              <Card className="glass-card">
                <CardContent className="p-4 text-center">
                  <Shield className="w-6 h-6 text-secondary mx-auto mb-2" />
                  <p className="text-2xl font-mono font-bold">{marketStats.lp_count}</p>
                  <p className="text-xs text-muted-foreground">Active LPs</p>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Wallet Connection */}
          {!isConnected && (
            <Card className="glass-card border-secondary/30 mb-8">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <AlertCircle className="w-8 h-8 text-secondary" />
                  <div className="flex-1">
                    <h3 className="font-semibold">Connect Wallet to Provide Liquidity</h3>
                    <p className="text-sm text-muted-foreground">
                      Connect your Xverse wallet to become an LP
                    </p>
                  </div>
                  <Button onClick={connectWallet} className="bg-secondary text-secondary-foreground">
                    Connect Wallet
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left: Slot Selection */}
            <div className="lg:col-span-2 space-y-6">
              {/* All Slots Option */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Droplets className="w-5 h-5 text-secondary" />
                    Select LP Strategy
                  </CardTitle>
                  <CardDescription>
                    Choose specific slots or provide to all slots at once
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* All Slots Button */}
                  <button
                    onClick={() => setSelectedSlot(0)}
                    disabled={loading}
                    className={`
                      w-full p-4 rounded-xl border-2 text-left transition-all mb-4
                      ${selectedSlot === 0 
                        ? 'border-secondary bg-secondary/10 shadow-lg' 
                        : 'border-border hover:border-secondary/50 bg-muted/30'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
                          <PieChart className="w-6 h-6 text-secondary" />
                        </div>
                        <div>
                          <p className="font-heading text-xl font-bold">All Slots (1-10)</p>
                          <p className="text-sm text-muted-foreground">
                            Diversified exposure across all risk levels
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-secondary border-secondary">
                        Recommended
                      </Badge>
                    </div>
                  </button>
                  
                  {/* Individual Slots Grid */}
                  <div className="grid md:grid-cols-2 gap-3">
                    {pools.map((pool) => (
                      <SlotCard
                        key={pool.slot}
                        pool={pool}
                        isSelected={selectedSlot === pool.slot}
                        onSelect={setSelectedSlot}
                        disabled={loading}
                        topLPs={lpSlotStats?.[`slot_${pool.slot}`]?.top_3_lps || []}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Right: Deposit & Positions */}
            <div className="space-y-6">
              {/* Deposit Card */}
              {isConnected && (
                <Card className="glass-card border-secondary/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="w-5 h-5 text-secondary" />
                      Add Liquidity
                    </CardTitle>
                    <CardDescription>
                      {selectedSlot === 0 
                        ? 'Adding to all slots (diversified)'
                        : `Adding to Slot ${selectedSlot}x`
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground mb-2 block">
                        Amount (STXOSHI)
                      </label>
                      <Input
                        type="number"
                        placeholder="Enter amount..."
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        className="bg-muted/50"
                      />
                    </div>
                    
                    {/* Quick Amount Buttons */}
                    <div className="flex gap-2">
                      {[100, 1000, 5000, 10000].map((amount) => (
                        <button
                          key={amount}
                          onClick={() => setDepositAmount(amount.toString())}
                          className="flex-1 py-1 px-2 rounded bg-muted/50 hover:bg-muted text-sm font-mono"
                        >
                          {amount >= 1000 ? `${amount / 1000}K` : amount}
                        </button>
                      ))}
                    </div>
                    
                    {/* Estimated Returns */}
                    {depositAmount && (
                      <div className="p-3 rounded-lg bg-secondary/10 border border-secondary/30">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Estimated APY</span>
                          <span className="text-green-400 font-mono">
                            ~{calculateEstimatedAPY(selectedSlot || 5)}%
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Based on current market volume. Actual returns depend on flipper activity.
                        </p>
                      </div>
                    )}
                    
                    {/* Risk Warning */}
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5" />
                        <div className="text-xs text-yellow-200">
                          <p className="font-semibold mb-1">LP Risk</p>
                          <p>You earn 97% of lost bets and 97% of the 3% vig. However, you pay winning bets from your liquidity.</p>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleDeposit}
                      disabled={loading || !depositAmount}
                      className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
                    >
                      {depositStatus === 'signing' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sign in Xverse Wallet...
                        </>
                      ) : depositStatus === 'confirming' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Confirming Deposit...
                        </>
                      ) : depositStatus === 'complete' ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Deposit Complete!
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Deposit & Confirm On-Chain
                        </>
                      )}
                    </Button>
                    
                    {/* Blue pulsing manual confirm button - always visible */}
                    <Button
                      onClick={handleManualConfirm}
                      variant="outline"
                      className="w-full mt-3 h-12 border-2 border-blue-500 text-blue-400 hover:bg-blue-500/20 font-medium"
                      style={{
                        animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)'
                      }}
                    >
                      <AlertCircle className="w-5 h-5 mr-2" />
                      Stuck on this page? Manual confirm deposit...
                    </Button>
                  </CardContent>
                </Card>
              )}
              
              {/* My Positions */}
              {isConnected && myPositions.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    Your LP Positions
                  </h3>
                  {myPositions.map((position) => (
                    <LPPositionCard
                      key={position.id}
                      position={position}
                      onWithdraw={handleWithdraw}
                      loading={withdrawingId === position.id}
                    />
                  ))}
                </div>
              )}
              
              {/* How It Works */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-sm">How LP Works</CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 flex-shrink-0">1</div>
                    <p><strong>Deposit:</strong> Lock STXOSHI in a slot (or all slots)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 flex-shrink-0">2</div>
                    <p><strong>Earn:</strong> LPs receive 100% of the Spread and act as the direct counterparty to all trades, less a 3% Platform Management Fee.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 flex-shrink-0">3</div>
                    <p><strong>Risk:</strong> Pay out winning bets from your LP (proportional to your share)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-secondary/20 flex items-center justify-center text-secondary flex-shrink-0">4</div>
                    <p><strong>Withdraw:</strong> Exit anytime with your deposit ± P&L</p>
                  </div>
                  <div className="flex items-start gap-2 mt-3 pt-3 border-t border-border">
                    <div className="w-5 h-5 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 flex-shrink-0">!</div>
                    <p><strong>Max Bet:</strong> Max Bet = Total Slot Liquidity ÷ Net Multiplier. This ensures the pool can always cover winning payouts.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
